import { useMemo } from 'react';
import { Card } from '../../../../../components/card/card';
import { Chip } from '../../../../../components/chip/chip';
import { Select } from '../../../../../components/select/select';
import { Table } from '../../../../../features/table/table';
import { TableFilterDateTimeRange } from '../../../../../features/table/table-filter/table-filter-date-time-range/table-filter-date-time-range';
import { TableFilterInput } from '../../../../../features/table/table-filter/table-filter-input/table-filter-input';
import { TableFilterMultiSelect } from '../../../../../features/table/table-filter/table-filter-multi-select/table-filter-multi-select';
import { formatRuDateTimeString } from '../../../../../helpers/date-time';
import { useBranchesStore } from '../../../../../store/use-branch-store';
import { useManagerStore } from '../../../../../store/use-manager-store';
import { useTaskStore } from '../../../../../store/use-task-store';
import { TasksInfo } from '../tasks-info/tasks-info';
import { statusTitleMap } from '../use-task-list';
import { useTaskListUtils } from '../use-task-list-utils';

export const TasksTable = () => {
  const { list, pagination, isLoadingFetch, updateTaskExecutor } = useTaskStore();
  const { place } = useBranchesStore();
  const {
    getEmployee,
    getEmployeeOptions,
    getStatusLabel,
    getStatusVariant,
    isStatusEditable,
    isDeletedEmployee,
    isCommon,
    t,
    getLocale,
  } = useTaskListUtils();
  const { hasAccess } = useManagerStore();

  const branches = useMemo(
    () => place?.map((item) => ({ id: String(item.id), value: getLocale(item.name) })) || [],
    [getLocale, place],
  );

  const handleChangeExecutor = async (task_id: number, executor_id: number) => {
    await updateTaskExecutor(task_id, { executor_id });
  };

  return (
    <Card>
      <Table
        id="task-list"
        columnSettings
        pagination={pagination}
        isLoading={isLoadingFetch}
        cols={{
          qrCode: isCommon
            ? {}
            : {
                label: 'QR',
                filter: (
                  <TableFilterInput
                    name="qrCode"
                    suggestOptions={list?.map((item) => getLocale(item.qr?.value))}
                  />
                ),
              },
          name: {
            label: t('Название'),
            filter: (
              <TableFilterInput
                name="name"
                suggestOptions={list?.map((item) => getLocale(item.title))}
              />
            ),
          },
          branch: {
            label: t('Филиал'),
            filter: <TableFilterMultiSelect name="branch" options={branches} />,
          },
          employee: {
            label: t('Ответственный'),
            filter: (
              <TableFilterInput
                name="employee"
                suggestOptions={list?.map((item) => getLocale(item.received_by.name))}
              />
            ),
          },
          status: {
            label: t('Статус'),
            filter: (
              <TableFilterMultiSelect
                name="status"
                options={Object.entries(statusTitleMap).map(([id, value]) => ({
                  id,
                  value: t(value),
                }))}
              />
            ),
          },
          createdAt: {
            label: t('Создано'),
            filter: <TableFilterDateTimeRange name="created_at" />,
          },
          doneAt: { label: t('Выполнено'), filter: <TableFilterDateTimeRange name="done_at" /> },
          info: {
            label: t('Инфо'),
          },
        }}
        rows={list?.map((item) => {
          const employee = getEmployee(item);
          const name = getLocale(item.title);
          const isRequiresExecutor = isStatusEditable(item.status) && isDeletedEmployee(employee);
          const isExecutorEditable = isStatusEditable(item.status) || isRequiresExecutor;

          const canWrite = hasAccess('task', ['write']);

          return {
            rowId: item.id,
            qrCode: getLocale(item.qr?.value),
            name: name,
            branch: getLocale(item.place_name),
            employee:
              isExecutorEditable && canWrite ? (
                <Select
                  value={employee}
                  options={getEmployeeOptions(item)}
                  onSelect={(value) => handleChangeExecutor(item.id, +value.id)}
                />
              ) : (
                employee?.value
              ),
            status: (
              <Chip variant={getStatusVariant(item.status, isRequiresExecutor)}>
                {getStatusLabel(item.status, isRequiresExecutor)}
              </Chip>
            ),
            createdAt: formatRuDateTimeString(item.created_at),
            doneAt: formatRuDateTimeString(item.resolved_at),
            info: <TasksInfo task={item} />,
          };
        })}
      />
    </Card>
  );
};
